import React from "react";
import styled, { css } from "styled-components";
import { useWindowDimensions } from "@gravitywelluk/react-hooks";
import {
  useDispatch,
  useMappedState
} from "redux-react-hook";

import { Button } from "src/library/components/Button";
import Grid from "src/library/components/Hedron";
import { breakpoint } from "src/library/utils";
import { IStore } from "src/core/shared/store/index";
import {
  getCurrentListQuantity,
  getCurrentQuantity,
  IProductMaybeWithSubstitution
} from "src/core/products/utils";
import useOrder from "src/core/orders/hooks/useOrder";
import useList from "src/core/lists/hooks/useList";
import {
  IProduct,
  ProductWithPackSizes
} from "src/core/products/api/products.api";
import useDaysToCutoff from "src/core/orders/hooks/validation/useDaysToCutoff";
import { updateOrderLine } from "src/core/orders/store/orders.actions";
import { updateListLine } from "src/core/lists/store/lists.actions";
import useGlobalSettings from "src/core/shared/hooks/useGlobalSettings";
import { Text } from "src/library/components/Text";
import { useDrop } from "src/core/drops/hooks";

import ProductQuantity, {
  IProductQuantityProps,
  ProductQuantityRef
} from "../ProductQuantity";
import useProductIsOrderable from "../../hooks/useProductIsOrderable";
import { useProductLeadTimeGroup } from "../../hooks/useProductLeadTimeGroup";
import { ProductPackSizeSelector } from "./ProductPackSizeSelector";
import { useProductLatestPackSize } from "../../hooks/useProductLatestPackSize";

export interface IQuantityProps {
  product: IProductMaybeWithSubstitution;
  originalProduct?: IProduct;
  unavailable?: string | null;
  layout?: IProductQuantityProps["layout"];
  onBlur?: IProductQuantityProps["onBlur"];
  onFocus?: IProductQuantityProps["onFocus"];
  productQuantityRef?: React.Ref<ProductQuantityRef>;
}

export const ProductQuantityButton: React.ReactFCWithChildren<IQuantityProps> = ({
  product, originalProduct, unavailable, productQuantityRef, layout, onBlur, onFocus
}) => {
  const { width } = useWindowDimensions();
  const { terminology } = useMappedState(({ global }: IStore) => ({ terminology: global.settings?.terminology }));
  const [ { currentOrder } ] = useOrder();
  const [ { currentList } ] = useList();
  const [ { globalSettings } ] = useGlobalSettings();
  const dispatch = useDispatch();
  const productCutoffDays = useDaysToCutoff();
  // Get substituted product if exists
  const substitution = React.useMemo(() => product.substitution, [ product ]);
  const [ startQuantity, setStartQuantity ] = React.useState<number>(1);
  const [ autoFocus, setAutoFocus ] = React.useState<boolean>(false);
  const orderable = useProductIsOrderable(originalProduct || product, unavailable);
  const productLeadTimeGroup = useProductLeadTimeGroup();
  const [ { packSize } ] = useProductLatestPackSize(product);

  const displayPackSize = React.useMemo(() => {
    return packSize?.code || product.unitCode || "unit";
  }, [ packSize?.code, product.unitCode ]);

  const canRemove = React.useMemo(() => {
    const leadTimeGroup = productLeadTimeGroup(product);

    if (currentList.data) {
      return true;
    } else if (currentOrder.data && currentOrder.data.orderDeliveryDate && leadTimeGroup) {
      // always allow remove on draft
      if (currentOrder.data.orderStatus?.settings.group === "DRAFT") {
        return true;
      }

      const daysToCut = productCutoffDays(currentOrder.data.orderDeliveryDate, leadTimeGroup);

      if (daysToCut === null || daysToCut < 0) {
        return false;
      } else {
        return true;
      }
    } else {
      return true;
    }
  }, [
    currentList.data,
    currentOrder.data,
    product,
    productCutoffDays,
    productLeadTimeGroup
  ]);

  const quantity = React.useMemo(() => {
    const productToUse = originalProduct || product;
    let quantity: number | null = null;

    if (currentList.data) {
      quantity = getCurrentListQuantity(productToUse.id, currentList.data) || null;
    } else {
      if (currentOrder.data) {
        quantity = getCurrentQuantity(productToUse.id, currentOrder.data) || null;
      }
    }

    if (packSize && quantity) {
      quantity = parseFloat((quantity / packSize.quantity).toFixed(2));
    }

    return quantity;
  }, [
    originalProduct,
    product,
    currentList.data,
    packSize,
    currentOrder.data
  ]);

  const isOrderOrList = React.useMemo(() => {
    if (currentList.data) {
      return "list";
    } else if (currentOrder.data) {
      return "order";
    }

    return null;
  }, [ currentList.data, currentOrder.data ]);

  /**
   * Amend the quantity of the product in redux
   *
   * @param quantity
   */
  const amendProductQuantityFunction = React.useCallback((newQuantity: number) => {
    const productToUse = originalProduct || product;

    if (packSize) {
      newQuantity = Math.floor(newQuantity * packSize.quantity);
    }

    setAutoFocus(true);

    if (isOrderOrList === "list") {
      dispatch(updateListLine(productToUse, newQuantity));
    } else if (isOrderOrList === "order") {
      dispatch(updateOrderLine(productToUse, newQuantity, substitution));
    }
  }, [
    originalProduct,
    product,
    packSize,
    isOrderOrList,
    dispatch,
    substitution
  ]);

  const OrderButton = React.useCallback(() => {
    if (!orderable) {
      if (!quantity) {
        return (
          <Button
            className="not-available"
            disabled
          >
            Not Available
          </Button>
        );
      } else {
        return (
          <ProductQuantity
            onBlur={onBlur}
            onFocus={onFocus}
            ref={productQuantityRef}
            initialQuantity={quantity}
            handleQuantityUpdate={newValue => amendProductQuantityFunction(newValue)}
            hasError
            autoFocus={autoFocus}
            canRemove={canRemove}
          />
        );
      }
    } else if (quantity || layout === "rapid") {
      return (
        <ProductQuantity
          onBlur={onBlur}
          onFocus={onFocus}
          ref={productQuantityRef}
          layout={layout}
          initialQuantity={quantity || 0}
          handleQuantityUpdate={newValue => amendProductQuantityFunction(newValue)}
          autoFocus={autoFocus}
          canRemove={canRemove}
        />
      );
    } else {
      return (
        <OrderButtonWrapper
          quantity={startQuantity}
          displayPackSize={displayPackSize}
          className={`add-product-wrapper ${globalSettings?.hasPackSizes ? "has-packsizes" : ""}`}
        >
          <div className="quantity-controls">

            <input
              className="start-quantity"
              placeholder="0"
              type="number"
              min={0}
              onChange={e => e.currentTarget.value.length ? setStartQuantity(parseInt(e.currentTarget.value)) : setStartQuantity(1)}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  amendProductQuantityFunction(startQuantity || 1);
                }
              }}
            />

            {product.packSizes?.length ? (
              <ProductPackSizeSelector
                product={product as ProductWithPackSizes}
                hideUnitQuantity
              />
            ) : globalSettings?.hasPackSizes && (
              <div className="unit-label">
                <Text>
                  {product.unitCode || "unit"}
                </Text>
              </div>
            )}

          </div>

          <Button
            onClick={() => {
              amendProductQuantityFunction(startQuantity || 1);
            }}
            background="primaryDark"
            className={globalSettings?.hasPackSizes ? "packsize-add-button" : ""}
          >
            <>
              {(width && width < 600) ? "Add " : `Add to ${terminology?.cart?.singular.text || "cart"}`}
            </>
          </Button>

          {packSize?.quantity && packSize.quantity > 1 && (
            <div className="pack-quantity-label">
              <Text fontSize="sub">
                {`Contains ${packSize.quantity} ${product.unitCode || "units"}`}
              </Text>
            </div>
          )}

        </OrderButtonWrapper>
      );
    }
  }, [
    orderable,
    quantity,
    layout,
    onBlur,
    onFocus,
    productQuantityRef,
    autoFocus,
    canRemove,
    amendProductQuantityFunction,
    startQuantity,
    displayPackSize,
    globalSettings?.hasPackSizes,
    product,
    width,
    terminology?.cart?.singular.text,
    packSize?.quantity
  ]);

  return (
    <ActionsWrapper className="order-control-wrapper">
      <>
        {OrderButton()}
      </>
    </ActionsWrapper>
  );
};

const ActionsWrapper = styled(Grid.Box)`
  ${({ theme }) => breakpoint("xs", theme)} {
    display: flex;
    align-items: center;
  }
`;

const OrderButtonWrapper = styled.div<{quantity: number, displayPackSize: string}>`
  ${({
    theme, quantity, displayPackSize
  }) => css`
    display: flex;
    align-items: center;

    .quantity-controls {
      display: flex;
      flex-direction: row;
    }

    &:not(.has-packsizes) {
      button {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
      }
    }

    &.has-packsizes {
      flex-direction: column;

      .button-wrapper {
        display: block;
        width: 100%;
      }

      .quantity-controls {
        margin-bottom: ${theme.spacing.margins.sm};
      }
    }

    .start-quantity, .select.select {
      width: 45px;
      height: 39px;
      border: 2px solid ${theme.colors.primaryDark};
      border-right: 0px;
      text-align: center;
      outline: none;
      border-top-left-radius: ${theme.borderRadius};
      border-bottom-left-radius: ${theme.borderRadius};
      padding-left: 15px;
      vertical-align: top;
      width: calc(20px + ${quantity.toString().length * 7}px); // dynamic get bigger
      font-size: 12px; // hard coded as needs to not change
      
      ${breakpoint("sm", theme)} {
        font-size: ${theme.fonts.sizes.p};
        width: calc(38px + ${quantity.toString().length * 7}px); // dynamic get bigger
      }


      @media (hover: none) and (pointer: coarse) {
        padding-left: 0px;
      }

    }

    .select.select {
      height: 39px;
      border: 2px solid ${theme.colors.primaryDark};
      border-right: 2px solid ${theme.colors.primaryDark};
      text-align: center;
      outline: none;
      border-radius: 0px;
      border-top-right-radius: ${theme.borderRadius};
      border-bottom-right-radius: ${theme.borderRadius};
      padding-left: ${theme.spacing.padding.xs};
      padding-right: ${theme.spacing.padding.lg};
      vertical-align: top;
      width: calc(55px + ${displayPackSize.length * 5}px); // dynamic get bigger
      min-width: 0px;
      font-size: 12px; // hard coded as needs to not change
    }



    button {
      margin: 0;
      width: 100%;
      background-color: ${theme.colors.white};
      color: ${theme.colors.primaryDark};
      border: 2px solid ${theme.colors.primaryDark};
      font-weight: ${theme.fonts.weights.bold};

      &.not-available {
        &:hover {
          cursor: not-allowed;
        }
      }
    }

    .unit-label {
      display: flex;
      justify-content: center;
      align-items: center;
      border-top-right-radius: ${theme.borderRadius};
      border-bottom-right-radius: ${theme.borderRadius};
      border: 2px solid ${theme.colors.primaryDark};
      width: calc(55px + ${displayPackSize.length * 5}px);
    }

    .condensed-rounded-button {
      width: fit-content;
      border-radius: ${theme.borderRadius};
    }

    .pack-quantity-label {
      display: flex;
      justify-content: flex-end;
    }
  `};
`;
